import { SimpleDatePipe } from 'src/app/pipes/simple-date.pipe';
import { CleanDatePipe } from 'src/app/pipes/clean-date.pipe';
import { User } from 'src/app/component/auth/model/user.model';
import { StudentTerminationStatusGraduation } from './../../../model/student-termination-status-graduation.model';
import { SchoolEnrollment } from './../../../model/school-enrollment.model';
import { StudentTerminationStatusType } from './../../../model/student-termination-status-type.model';
import { StudentTerminationStatus } from './../../../model/student-termination-status.model';
import { Component, EventEmitter, Input, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { StudentTerminationsService } from '../../../service/student-terminations.service';
import { Student } from 'src/app/model/student/student.model';
import { TitleCasePipe } from '@angular/common';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { GetPropertyPipe } from 'src/app/pipes/get-property.pipe';
import { LoginTokenService } from 'src/app/service/login-token.service';
import { StudentHelperService } from '../../../service/student-helper.service';

@Component({
    selector: 'edit-child-demographic-termination',
    templateUrl: './edit-child-demographic-termination.component.html',
    styleUrls: ['./edit-child-demographic-termination.component.scss']
})
export class EditChildDemographicTerminationComponent implements OnInit {
    @Input() studentId!: number;
    @Input() editing: boolean;
    @Output() editingChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('terminationForm') terminationForm: FormGroupDirective;
    editingForm!: UntypedFormGroup;
    editingId: number | undefined;

    studentTerminationStatusesBackup: StudentTerminationStatus[] = [];
    studentTerminationStatuses: StudentTerminationStatus[] = [];
    studentTerminationStatusesToCreate: StudentTerminationStatus[] = [];
    studentTerminationStatusesToDelete: StudentTerminationStatus[] = [];

    studentTerminationStatusTypes: StudentTerminationStatusType[] = [];
    schoolEnrollments: SchoolEnrollment[] = [];
    studentTerminationStatusFound: boolean = false;
    schoolEnrollmentRequired: boolean = false;

    currentUser: User;

    isLoading: boolean = false;
    isSaving: boolean = false;
    adding: boolean = false;

    promises: Array<any> = [];

    // Table display
    newDisplayedColumns: string[] = ['ADD'];
    displayedColumns: string[] = ['EDIT', 'STATUS_DATE', 'STUDENT_TERMINATION_STATUS_TYPES.TYPE', 'CREATED_BY', 'CREATED_DATE'];

    // Table results
    tableDataSource: MatTableDataSource<StudentTerminationStatus>;

    // Changes
    formChanges: any = {};

    getPropertyPipe = new GetPropertyPipe();

    subs: Subscription[] = [];

    constructor(
        private fb: UntypedFormBuilder,
        private studentTerminationsService: StudentTerminationsService,
        private loginTokenService: LoginTokenService,
        private cleanDatePipe: CleanDatePipe,
        private simpleDatePipe: SimpleDatePipe,
        private studentHelperService: StudentHelperService
    ) {
        this.editingForm = fb.group({
            terminationDate: ['', [Validators.required]],
            terminationReason: ['', [Validators.required]],
            terminationGraduationEnrollment: ['']
        });
    }

    ngOnInit(): void {}

    init() {
        this.isLoading = true;
        this.studentTerminationStatusesToCreate = [];
        this.studentTerminationStatusesToDelete = [];
        this.cancelAddStudentTerminationStatus();

        this.getStudentTerminationStatuses();
        this.getStudentTerminationStatusTypes();
        this.getSchoolEnrollments();
        this.getCurrentUser();
        Promise.all(this.promises).then((res) => {
            this.updateDataSource();

            // Set Termination Reversed to unselectable if no termination exists
            let index = this.studentTerminationStatusTypes.findIndex((stst) => stst.TYPE === 'Termination Reversed');

            if (index >= 0) {
                this.studentTerminationStatusTypes[index].SELECTABLE =
                    this.studentTerminationStatuses.length > 0 && this.studentTerminationStatuses[0].STUDENT_TERMINATION_STATUS_TYPES.ID !== 1 ? 1 : 0;
            }

            if (
                this.studentTerminationStatuses[0] &&
                this.studentTerminationStatuses[0].ID &&
                this.studentTerminationStatuses[0].STUDENT_TERMINATION_STATUS_TYPES.ID !== 1
            ) {
                this.studentHelperService.setTerminated('Terminated');
            } else {
                this.studentHelperService.setTerminated('');
            }

            this.isLoading = false;
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.editing) {
            this.displayedColumns = ['EDIT', 'STATUS_DATE', 'STUDENT_TERMINATION_STATUS_TYPES.TYPE', 'CREATED_BY', 'CREATED_DATE'];
            this.editingForm.enable();
        } else {
            this.displayedColumns = ['STATUS_DATE', 'STUDENT_TERMINATION_STATUS_TYPES.TYPE', 'CREATED_BY', 'CREATED_DATE'];
            this.editingForm.disable();
            if (changes['editing']) {
                this.init();
            }
        }
    }

    ngOnDestroy() {
        this.subs.forEach((sub) => {
            sub.unsubscribe();
        });
    }

    subscribers() {
        let formSub = this.editingForm.valueChanges.subscribe(() => {
            let changes = this.editingForm.getRawValue();
            Object.keys(changes).forEach((key) => {
                this.formChanges[key].CHANGED_VALUE = changes[key];
            });

            this.updateDataSource();
        });
        this.subs.push(formSub);
    }

    getStudentTerminationStatuses() {
        let deferred = new Promise((resolve, reject) => {
            this.studentTerminationsService.getStudentTerminationStatusHistory(this.studentId).subscribe((res: any) => {
                if (res.studentTerminationStatuses) {
                    this.studentTerminationStatuses = res.studentTerminationStatuses;

                    this.studentTerminationStatuses.forEach((sts) => {
                        sts.STATUS_DATE = this.cleanDatePipe.transform(sts.STATUS_DATE);
                        sts.CREATED_DATE = this.cleanDatePipe.transform(sts.CREATED_DATE);
                    });

                    this.studentTerminationStatuses.sort((n1, n2) => {
                        const createdDiff = new Date(n2.CREATED_DATE).getTime() - new Date(n1.CREATED_DATE).getTime();

                        if (createdDiff !== 0) {
                            return createdDiff;
                        }

                        return new Date(n2.STATUS_DATE).getTime() - new Date(n1.STATUS_DATE).getTime();
                    });

                    resolve(res);
                } else if (res?.msg) {
                    resolve(res);
                } else {
                    reject('err');
                }
            });
        });

        this.promises.push(deferred);
    }

    getStudentTerminationStatusTypes() {
        let deferred = new Promise((resolve, reject) => {
            this.studentTerminationsService.getStudentTerminationStatusTypes().subscribe((res: any) => {
                if (res.studentTerminationStatusTypes) {
                    this.studentTerminationStatusTypes = res.studentTerminationStatusTypes;

                    resolve(res);
                } else if (res?.msg) {
                    reject(res);
                } else {
                    reject('err');
                }
            });
        });

        this.promises.push(deferred);
    }

    getSchoolEnrollments() {
        let deferred = new Promise((resolve, reject) => {
            this.studentTerminationsService.getSchoolEnrollments(this.studentId).subscribe((res: any) => {
                if (res.schoolEnrollments) {
                    this.schoolEnrollments = res.schoolEnrollments;

                    resolve(res);
                } else if (res?.msg) {
                    resolve(res);
                } else {
                    reject('err');
                }
            });
        });

        this.promises.push(deferred);
    }

    getCurrentUser() {
        this.currentUser = this.loginTokenService.getUser();
    }

    submit() {
        if (this.editingId) {
            return new Promise((resolve, reject) => {
                reject({ MSG: 'Please finish editing Student Termination Status.', SUCCESS: false });
            });
        } else if (this.studentTerminationStatusesToCreate.length > 0 || this.studentTerminationStatusesToDelete.length > 0) {
            this.isSaving = true;
            this.promises = [];

            this.studentTerminationStatusesToCreate.forEach((studentTerminationStatus) => {
                this.createStudentTerminationStatuses(studentTerminationStatus);
            });

            if (this.studentTerminationStatusesToDelete.length > 0) {
                this.deleteStudentTerminationStatuses(this.studentTerminationStatusesToDelete);
            }

            return new Promise((resolve, reject) => {
                Promise.all(this.promises).then(
                    (res) => {
                        this.ngOnInit();
                        this.setNewStudentTerminationStatusFound(false);
                        this.isSaving = false;
                        resolve({ MSG: 'Student Termination Statuses succcessfuly updated.', SUCCESS: true });
                    },
                    (err) => {
                        this.isSaving = false;
                        reject({ MSG: 'Student Termination Statuses has errors.', SUCCESS: false });
                    }
                );
            });
        } else {
            return new Promise((resolve, reject) => {
                resolve({});
            });
        }
    }

    createStudentTerminationStatuses(studentTerminationStatus: StudentTerminationStatus) {
        let newStudent = new Student();
        newStudent.ID = this.studentId;

        newStudent.STUDENT_TERMINATION_STATUSES = [studentTerminationStatus];

        let deferred = new Promise((resolve, reject) => {
            this.studentTerminationsService.create(newStudent).subscribe((res) => {
                if (res?.msg) {
                    if (res.msg.SUCCESS) {
                        resolve(res);
                    } else {
                        reject(res);
                    }
                } else {
                    reject('err');
                }
            });
        });

        this.promises.push(deferred);
    }

    deleteStudentTerminationStatuses(studentTerminationStatusesToDelete: StudentTerminationStatus[]) {
        let deferred = new Promise((resolve, reject) => {
            this.studentTerminationsService.deleteStudentTerminations(studentTerminationStatusesToDelete).subscribe((res) => {
                if (res?.msg) {
                    if (res.msg.SUCCESS) {
                        resolve(res);
                    } else {
                        reject(res);
                    }
                } else {
                    reject('err');
                }
            });
        });

        this.promises.push(deferred);
    }

    updateDataSource() {
        setTimeout(() => {
            // Data
            let dataSource = new MatTableDataSource<StudentTerminationStatus>();
            dataSource.data = this.studentTerminationStatuses;

            this.tableDataSource = dataSource;
        });
    }

    private setNewStudentTerminationStatusFound(found: boolean) {
        if (found) {
            this.studentTerminationStatusFound = found;
        } else {
            this.studentTerminationStatusFound = found;
        }
    }

    // Edit Functions
    addStudentTerminationStatus() {
        this._setAdding();
        this.adding = true;
    }
    confirmAddStudentTerminationStatus() {
        this._confirmAddStudentTerminationStatus(this.editingForm);
        this.updateDataSource();
        this.setNewStudentTerminationStatusFound(true);
        this._cancelEdit();
    }
    cancelAddStudentTerminationStatus() {
        this._cancelEdit();
    }
    deleteStudentTerminationStatus(studentTerminationStatus: StudentTerminationStatus) {
        this._confirmDeleteStudentTerminationStatus(studentTerminationStatus);
        this.updateDataSource();
        this.setNewStudentTerminationStatusFound(false);
    }

    // Private action Functions
    private _cancelEdit() {
        this._resetEditing();
    }

    private _setAdding() {
        this.newDisplayedColumns = ['ADD_EDIT', 'ADD_STATUS_DATE', 'ADD_STUDENT_TERMINATION_STATUS_TYPES.TYPE'];
        this.editingForm.patchValue({
            terminationDate: '',
            terminationReason: '',
            terminationGraduationEnrollment: ''
        });
        this.editingForm.markAsPristine();
    }

    private _resetEditing() {
        this.newDisplayedColumns = ['ADD'];
        this.editingForm.patchValue({ lastName: '', firstName: '', preferredPhoneNumber: '', preferredEmail: '' });

        this.adding = false;
        this.schoolEnrollmentRequired = false;
    }

    private _confirmAddStudentTerminationStatus(form: UntypedFormGroup) {
        if (form.valid) {
            let newEdit = new StudentTerminationStatus();
            newEdit.ID = 0;
            newEdit.STATUS_DATE = form.get('terminationDate')?.value;
            newEdit.STUDENT_TERMINATION_STATUS_TYPES = form.get('terminationReason')?.value;
            newEdit.STUDENT_TERMINATION_STATUS_TYPES_ID = newEdit.STUDENT_TERMINATION_STATUS_TYPES.ID;
            newEdit.CREATED_BY = this.currentUser.USER_ID!;

            switch (newEdit.STUDENT_TERMINATION_STATUS_TYPES.TYPE) {
                case 'Graduated':
                case 'High School Equivalency Passed':
                    if (form.get('terminationGraduationEnrollment')?.value) {
                        let schoolEnrollment: SchoolEnrollment = form.get('terminationGraduationEnrollment')?.value;
                        let studentTerminationStatusGraduation: StudentTerminationStatusGraduation = new StudentTerminationStatusGraduation();
                        studentTerminationStatusGraduation.SCHOOL_ENROLLMENT = schoolEnrollment;
                        studentTerminationStatusGraduation.SCHOOL_ENROLLMENTS_ID = schoolEnrollment.ID;

                        newEdit.STUDENT_TERMINATION_STATUS_GRADUATIONS = [studentTerminationStatusGraduation];
                    }
                    break;
                default:
                    break;
            }

            this.studentTerminationStatusesToCreate.push(newEdit);
            this.studentTerminationStatuses.push(newEdit);

            this.studentTerminationStatuses.sort((n1: StudentTerminationStatus, n2: StudentTerminationStatus) => (n1.STATUS_DATE! > n2.STATUS_DATE! ? -1 : 1));
        }
    }

    private _confirmDeleteStudentTerminationStatus(studentTerminationStatus: StudentTerminationStatus) {
        let index: number;
        if (studentTerminationStatus.ID > 0) {
            index = this.studentTerminationStatusesToDelete.findIndex((sts) => sts.ID === studentTerminationStatus.ID);

            if (index < 0) {
                this.studentTerminationStatusesToDelete.push(studentTerminationStatus);
            }
        }
        index = this.studentTerminationStatuses.findIndex((sts) => sts.ID === studentTerminationStatus.ID);

        this.studentTerminationStatuses.splice(index, 1);

        index = this.studentTerminationStatusesToCreate.findIndex((sts) => sts.ID === studentTerminationStatus.ID);

        this.studentTerminationStatusesToCreate.splice(index, 1);
    }

    getFormValue(formControlName: string, path: string): string {
        return this.editingForm.get(formControlName)?.value[path];
    }

    checkTerminationStatusType() {
        if (this.editingForm.get('terminationReason')) {
            switch (this.editingForm.get('terminationReason')!.value.TYPE) {
                case 'Graduated':
                case 'High School Equivalency Passed':
                    this.editingForm.get('terminationGraduationEnrollment')?.setValidators([Validators.required]);
                    this.editingForm.get('terminationGraduationEnrollment')?.updateValueAndValidity();
                    this.schoolEnrollmentRequired = true;
                    break;
                default:
                    this.editingForm.get('terminationGraduationEnrollment')?.setValidators([]);
                    this.editingForm.get('terminationGraduationEnrollment')?.updateValueAndValidity();
                    this.schoolEnrollmentRequired = false;
                    break;
            }
        } else {
            this.editingForm.get('terminationGraduationEnrollment')?.setValidators([Validators.required]);
            this.editingForm.get('terminationGraduationEnrollment')?.updateValueAndValidity();
            this.schoolEnrollmentRequired = false;
        }
    }

    /***************************************** Confirmation Changes */
    initCurrentChanges() {}

    setCurrentChanges() {}

    updateChanges() {
        this.formChanges = {};

        this.studentTerminationStatusesToCreate.forEach((sts, index) => {
            this.formChanges[sts.STUDENT_TERMINATION_STATUS_TYPES.TYPE + index.toString()] = {
                CHANGED_FIELD: 'New Termination',
                VALUE: '',
                CHANGED_VALUE: sts.STUDENT_TERMINATION_STATUS_TYPES.TYPE + ' (' + this.simpleDatePipe.transform(sts.STATUS_DATE) + ')'
            };
        });

        this.studentTerminationStatusesToDelete.forEach((sts, index) => {
            this.formChanges[sts.STUDENT_TERMINATION_STATUS_TYPES.TYPE + index.toString()] = {
                CHANGED_FIELD: 'Delete Termination',
                VALUE: sts.STUDENT_TERMINATION_STATUS_TYPES.TYPE + ' (' + this.simpleDatePipe.transform(sts.STATUS_DATE) + ')',
                CHANGED_VALUE: ''
            };
        });

        Object.keys(this.formChanges).forEach((key) => {
            this.formChanges[key].CHANGED = this.formChanges[key].VALUE !== this.formChanges[key].CHANGED_VALUE;
        });
    }

    getChanges() {
        this.updateChanges();
        let changes: any[] = [];

        Object.keys(this.formChanges).forEach((key) => {
            if (this.formChanges[key].CHANGED) {
                changes.push(this.formChanges[key]);
            }
        });

        return changes;
    }

    getTrackingHistories() {
        let trackingHistories: any[] = [];

        Object.keys(this.formChanges).forEach((key) => {
            if (this.formChanges[key].CHANGED) {
                let trackingHistory = this.formChanges[key];
                trackingHistory.CHANGED_BY = this.currentUser.USER_ID;
                trackingHistory.CHANGED_FORM = 'Student Terminations';
                trackingHistory.CHANGED_FORM_ID = this.studentId;
                trackingHistory.CHANGED_VALUE = trackingHistory.VALUE; // VALUE holds current value, CHANGED_VALUE is used in back-end to record previous data.

                trackingHistories.push(trackingHistory);
            }
        });

        return { editedFields: trackingHistories };
    }
}
